import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { CalendarClock, Pencil, Check, X } from 'lucide-react';
import { fmtDate } from '../../lib/dates';
import { DateField } from '../DateField';

export const HEARING_TYPES = ['Audiencia preparatoria', 'Audiencia de juicio', 'Alegatos', 'Conciliación', 'Audiencia de prueba', 'Lectura de sentencia', 'Otra'];

const fmt = (v) => fmtDate(v, true);

export const HearingRow = ({ matter, onUpdated }) => {
  const { api } = useAuth();
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(matter.next_hearing || '');
  const [type, setType] = useState(matter.hearing_type || HEARING_TYPES[0]);
  useEffect(() => {
    setDate(matter.next_hearing || '');
    setType(matter.hearing_type || HEARING_TYPES[0]);
  }, [matter.next_hearing, matter.hearing_type]);

  const clear = async () => {
    try {
      await api.patch(`/crm/matters/${matter.id}`, { next_hearing: '' });
      toast.success('Audiencia eliminada');
      setEditing(false);
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const save = async () => {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(date)) return toast.error('Elige fecha y hora');
    try {
      await api.patch(`/crm/matters/${matter.id}`, { next_hearing: date, hearing_type: type });
      toast.success('Audiencia guardada. Te avisaremos 3 días y 1 día antes.');
      setEditing(false);
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    }
  };

  if (editing) {
    return (
      <div data-testid={`hearing-editor-${matter.id}`} className="mt-3 space-y-2 bg-slate-800/60 rounded-lg p-3">
        <div className="flex gap-2">
          <DateField testid={`hearing-date-input-${matter.id}`} value={date.slice(0, 10)} onChange={(d) => setDate(d ? `${d}T${date.slice(11, 16) || '09:00'}` : '')} className="flex-1 py-2" />
          <input data-testid={`hearing-time-input-${matter.id}`} type="time" value={date.slice(11, 16)} onChange={(e) => setDate(date.slice(0, 10) ? `${date.slice(0, 10)}T${e.target.value}` : '')} className="bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
        </div>
        <select data-testid={`hearing-type-select-${matter.id}`} value={type} onChange={(e) => setType(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
          {HEARING_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="flex gap-2 justify-end">
          {matter.next_hearing && <button data-testid={`hearing-clear-btn-${matter.id}`} onClick={clear} className="text-xs text-red-400 mr-auto px-2 py-1">Quitar audiencia</button>}
          <button onClick={() => setEditing(false)} className="text-xs text-slate-400 flex items-center gap-1 px-2 py-1"><X className="w-3 h-3" />Cancelar</button>
          <button data-testid={`hearing-save-btn-${matter.id}`} onClick={save} className="text-xs bg-cyan-500 text-slate-950 font-semibold rounded-lg px-3 py-1 flex items-center gap-1"><Check className="w-3 h-3" />Guardar</button>
        </div>
      </div>
    );
  }

  const upcoming = matter.next_hearing && new Date(matter.next_hearing) > new Date();
  return (
    <div className="mt-2 flex items-start justify-between gap-2">
      <p data-testid={`matter-hearing-${matter.id}`} className={`text-sm flex items-start gap-2 min-w-0 ${upcoming ? 'text-cyan-300' : 'text-slate-500'}`}>
        <CalendarClock className="w-4 h-4 shrink-0 mt-0.5" />
        <span className="break-words">{matter.next_hearing ? `${fmt(matter.next_hearing)} · ${matter.hearing_type || 'Audiencia'}` : 'Sin audiencia registrada'}</span>
      </p>
      <button data-testid={`hearing-edit-btn-${matter.id}`} onClick={() => setEditing(true)} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 shrink-0">
        <Pencil className="w-3 h-3" />{matter.next_hearing ? 'Cambiar' : 'Agendar'}
      </button>
    </div>
  );
};
